<script lang="ts">
	import { createAccordion, melt } from '@melt-ui/svelte';
	import { CheckIcon, ChevronDown, ChevronUp, Frown, Smile, X } from 'lucide-svelte';
	import { slide } from 'svelte/transition';
	import Button from './Button.svelte';
	import Collapsible from './Collapsible.svelte';

	interface Props {
		sufficientEmergencyFund: boolean | undefined;
		sufficientSavings: boolean | undefined;
		sufficientCiInsurance: boolean | undefined;
		sufficientTpdInsurance: boolean | undefined;
		sufficientInsuranceSpend: boolean | undefined;
		sufficientIncomeInvestment: boolean | undefined;
		sufficientNetworthInvestment: boolean | undefined;
		sufficientCpfForFrs: boolean | undefined;
		multipleIncomeStreams: boolean | undefined;
	}

	let {
		sufficientEmergencyFund,
		sufficientSavings,
		sufficientCiInsurance,
		sufficientTpdInsurance,
		sufficientInsuranceSpend,
		sufficientIncomeInvestment,
		sufficientNetworthInvestment,
		sufficientCpfForFrs,
		multipleIncomeStreams
	}: Props = $props();

	const {
		elements: { content, item, trigger, root },
		helpers: { isSelected }
	} = createAccordion({
		multiple: true
	});

	function calculateScore(total: number) {
		let title = '';
		let description = '';
		if (total <= 1) {
			title = "You've taken the first step!";
			description =
				'Start your journey towards financial wellness by practicing good money habits ';
		} else if (total <= 3) {
			title = 'Great start!';
			description =
				"You're building a solid financial foundation. Keep practicing good money habits to improve your financial well-being!";
		} else if (total <= 5) {
			title = 'Good progress!';
			description =
				'Keep the momentum going! Stay consistent in practicing your good money habits to enhance your financial well-being.';
		} else if (total <= 8) {
			title = 'Well done!';
			description =
				"You're achieving strong results! Keep reinforcing those good money habits to maintain your financial well-being!";
		} else {
			title = 'Fantastic!';
			description =
				"You've covered all areas. Reinforce your good money habits for lasting success.";
		}
		return {
			total,
			title,
			description
		};
	}
	let saveScore = $derived([sufficientEmergencyFund, sufficientSavings].filter(Boolean).length);
	let protectScore = $derived(
		[sufficientCiInsurance, sufficientTpdInsurance, sufficientInsuranceSpend].filter(Boolean).length
	);
	let investScore = $derived(
		[sufficientIncomeInvestment, sufficientNetworthInvestment].filter(Boolean).length
	);
	let retireScore = $derived([sufficientCpfForFrs, multipleIncomeStreams].filter(Boolean).length);
	let { total, title, description } = $derived(
		calculateScore(saveScore + protectScore + investScore + retireScore)
	);
</script>

<div class="text-white-pure rounded-t-2xl bg-[#007dcb] p-5 text-center">
	<p class="h2 mb-5">{title}</p>
	<p class="mb-3">Your score is</p>
	<p class="mb-3 text-5xl font-medium text-[#e5af12] lg:text-6xl">{total}</p>
	<p class="mb-5">out of 9</p>
	<p>{description}</p>
</div>
<div class="border-gray-faint mb-5 rounded-b-2xl border-1 p-5">
	<header class="card-bold mb-6">Quick Insights</header>

	<ul class="accordion" {...$root}>
		<li use:melt={$item('save')}>
			<button use:melt={$trigger('save')}>
				<div>
					{#if saveScore === 2}
						<Smile class="text-green-ticker w-6" />
					{:else}
						<Frown class="text-red-ticker w-6" />
					{/if}
					<p>Save: {saveScore}/2</p>
				</div>

				<div>
					{#if $isSelected('save')}
						<ChevronUp class="w-6" />
					{:else}
						<ChevronDown class="w-6" />
					{/if}
				</div>
			</button>
			{#if $isSelected('save')}
				<ul use:melt={$content('save')} transition:slide class="content">
					<li>
						{#if sufficientEmergencyFund}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>Have 3-6 months of expenses emergency cash</p>
					</li>
					<li>
						{#if sufficientSavings}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>Save 10% of income per month</p>
					</li>
				</ul>
			{/if}
		</li>
		<li use:melt={$item('protect')}>
			<button use:melt={$trigger('protect')}>
				<div>
					{#if protectScore === 3}
						<Smile class="text-green-ticker w-6" />
					{:else}
						<Frown class="text-red-ticker w-6" />
					{/if}
					<p>Protect: {protectScore}/3</p>
				</div>

				<div>
					{#if $isSelected('protect')}
						<ChevronUp class="w-6" />
					{:else}
						<ChevronDown class="w-6" />
					{/if}
				</div>
			</button>
			{#if $isSelected('protect')}
				<ul use:melt={$content('protect')} transition:slide class="content">
					<li>
						{#if sufficientCiInsurance}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>Have insurance protection of 4 times annual income for critical illness.</p>
					</li>
					<li>
						{#if sufficientTpdInsurance}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>
							Have insurance protection of 9 times annual income for death and total and permanent
							disability.
						</p>
					</li>
					<li>
						{#if sufficientInsuranceSpend}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>Spend at most 15% of income on insurance protection</p>
					</li>
				</ul>
			{/if}
		</li>
		<li use:melt={$item('invest')}>
			<button use:melt={$trigger('invest')}>
				<div>
					{#if investScore === 2}
						<Smile class="text-green-ticker w-6" />
					{:else}
						<Frown class="text-red-ticker w-6" />
					{/if}
					<p>Invest: {investScore}/2</p>
				</div>

				<div>
					{#if $isSelected('invest')}
						<ChevronUp class="w-6" />
					{:else}
						<ChevronDown class="w-6" />
					{/if}
				</div>
			</button>
			{#if $isSelected('invest')}
				<ul use:melt={$content('invest')} transition:slide class="content">
					<li>
						{#if sufficientIncomeInvestment}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>Invest at least 10% of income for retirement and other financial goals</p>
					</li>
					<li>
						{#if sufficientNetworthInvestment}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>Have at least 50% of net worth invested</p>
					</li>
				</ul>
			{/if}
		</li>
		<li use:melt={$item('retire')}>
			<button use:melt={$trigger('retire')}>
				<div>
					{#if retireScore === 2}
						<Smile class="text-green-ticker w-6" />
					{:else}
						<Frown class="text-red-ticker w-6" />
					{/if}
					<p>Retire: {retireScore}/2</p>
				</div>

				<div>
					{#if $isSelected('retire')}
						<ChevronUp class="w-6" />
					{:else}
						<ChevronDown class="w-6" />
					{/if}
				</div>
			</button>
			{#if $isSelected('retire')}
				<ul use:melt={$content('retire')} transition:slide class="content">
					<li>
						{#if sufficientCpfForFrs}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>Meet CPF Full Retirement Sum</p>
					</li>
					<li>
						{#if multipleIncomeStreams}
							<CheckIcon class="text-green-ticker w-6" />
						{:else}
							<X class="text-red-ticker w-6" />
						{/if}
						<p>Have multiple streams of passive income</p>
					</li>
				</ul>
			{/if}
		</li>
	</ul>
</div>
<p class="mb-5 text-center">
	<span class="body-bold">Next Step:</span> Discover valuable tips from POSB for building money habits
	that lead to financial wellness
</p>

<Button
	to={{
		href: 'http://growbeansprout.link/dbs-4mh',
		target: '_blank',
		rel: 'noreferrer noopener nofollow'
	}}
>
	START NOW
</Button>
<Collapsible title="See full disclaimer" class="mt-5">
	<p class="small-regular text-[#909090]">
		This tool is designed to generate ideas for your finances but does not provide a comprehensive
		review of your financial situation or constitute financial advice. Please consult a financial
		advisor if you require personalised guidance.
	</p>
</Collapsible>

<style lang="postcss">
	@reference '../../../app.css';

	.accordion {
		@apply divide-gray-faint divide-y-1;

		button {
			@apply flex w-full items-center justify-between gap-4 py-4;

			& > div:first-child {
				@apply body-bold flex items-center gap-2;
			}
		}
	}

	.content {
		@apply card-regular text-gray-dark mb-4 grid gap-2;

		li {
			@apply grid grid-cols-[1.5rem_auto] items-start gap-2;
		}
	}
</style>
